<template>
  <profile-edit-section-card
    v-if="localState"
    title="Other services"
    breadcrumb-label="Profile"
    :saving="isSaving"
    :has-unsaved-changes="hasUnsavedChanges"
    @breadcrumb-click="cancelEdit"
    @save="handleSave"
    @cancel="cancelEdit"
  >
    <fieldset class="mb-24">
      <legend class="form__label pb-8">General services</legend>
      <p class="text-caption mb-16">Select all that apply</p>
      <ul>
        <li v-for="option in generalServiceOptions" :key="option.key" class="mb-8">
          <poppins-checkbox v-model="localState[option.key]" :checkbox-id="option.key" :label-name="option.label" />
        </li>
        <li v-for="option in additionalServiceOptions" :key="option.value" class="mb-8">
          <poppins-checkbox
            v-model="localState.additionalServices"
            :checkbox-id="option.value"
            :label-name="option.label"
            :value="option.value"
          />
        </li>
      </ul>
    </fieldset>
    <fieldset v-if="localState.infantCare">
      <legend class="form__label pb-8">Infant care services</legend>
      <p class="text-caption mb-16">Select all that apply</p>
      <poppins-dropdown
        id="infant-care-experience"
        v-model="localState.infantCareExperience"
        label-name="Years of infant care experience"
        :options="infantCareExperienceOptions"
        class="mb-16"
      />
      <ul>
        <li v-for="option in infantCareServiceOptions" :key="option.value" class="mb-8">
          <poppins-checkbox
            v-model="localState.infantCareServices"
            :checkbox-id="option.value"
            :label-name="option.label"
            :value="option.value"
          />
        </li>
      </ul>
    </fieldset>
  </profile-edit-section-card>
</template>

<script lang="ts">
import PoppinsCheckbox from '@/components/checkbox/Checkbox.vue';
import PoppinsDropdown from '@/components/dropdown/Dropdown.vue';
import ProfileEditSectionCard from '../../components/ProfileEditSectionCard.vue';
import profileEditMixin from '../../mixins/profileEditMixin';
import {
  CHILD_CARE_ADDITIONAL_SERVICE_OPTIONS,
  CHILD_CARE_GENERAL_SERVICE_OPTIONS,
  INFANT_CARE_EXPERIENCE_OPTIONS,
  INFANT_CARE_SERVICE_OPTIONS,
} from '../../__types__/CareTypeFormOptions';
import type { BabysitterProfileFormState } from '../../__types__/BabysitterProfileTypes';

const ADDITIONAL_SERVICES_FROM_BOOLEANS = {
  willingToDriveChildren: 'DRIVE_CHILDREN',
  willingToRunErrands: 'RUN_ERRANDS',
  twins: 'TWINS',
  sickChildren: 'SICK_CARE',
} as const;

const BOOLEAN_ADDITIONAL_SERVICE_CODES: Set<string> = new Set(Object.values(ADDITIONAL_SERVICES_FROM_BOOLEANS));

export default {
  name: 'ChildCareServicesForm',
  components: {
    ProfileEditSectionCard,
    PoppinsCheckbox,
    PoppinsDropdown,
  },
  mixins: [profileEditMixin],
  data(): any {
    return {
      localState: null,
      originalState: null,
    };
  },
  computed: {
    generalServiceOptions: () => CHILD_CARE_GENERAL_SERVICE_OPTIONS,
    additionalServiceOptions: () => CHILD_CARE_ADDITIONAL_SERVICE_OPTIONS,
    infantCareExperienceOptions: () => INFANT_CARE_EXPERIENCE_OPTIONS,
    infantCareServiceOptions: () => INFANT_CARE_SERVICE_OPTIONS,
    hasUnsavedChanges(): boolean {
      if (!this.localState || !this.originalState) return false;
      return JSON.stringify(this.localState) !== JSON.stringify(this.originalState);
    },
  },
  watch: {
    $route() {
      this.initializeFormState();
    },
    formState: {
      handler(newState) {
        if (newState) {
          this.initializeFormState();
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.initializeFormState();
  },
  methods: {
    initializeFormState() {
      const state = this.formState as BabysitterProfileFormState | null;
      if (!state) return;

      const additionalServices = (state.additionalServices || []).filter(
        (service) => !BOOLEAN_ADDITIONAL_SERVICE_CODES.has(service)
      );

      this.localState = {
        homeworkHelp: state.homeworkHelp || false,
        willingToDriveChildren: state.willingToDriveChildren || false,
        willingToRunErrands: state.willingToRunErrands || false,
        comfortableWithPets: state.comfortableWithPets || false,
        sickChildren: state.sickChildren || false,
        infantCare: state.infantCare || false,
        specialNeeds: state.specialNeeds || false,
        twins: state.twins || false,
        accessToTransportation: state.accessToTransportation || false,
        additionalServices,
        infantCareExperience: state.infantCareExperience || '',
        infantCareServices: [...(state.infantCareServices || [])],
      };
      this.originalState = JSON.parse(JSON.stringify(this.localState));
    },
    buildAdditionalServices(): string[] {
      const additionalServices = [...this.localState.additionalServices];
      (Object.keys(ADDITIONAL_SERVICES_FROM_BOOLEANS) as Array<keyof typeof ADDITIONAL_SERVICES_FROM_BOOLEANS>).forEach((key) => {
        if (this.localState[key]) {
          additionalServices.push(ADDITIONAL_SERVICES_FROM_BOOLEANS[key]);
        }
      });
      return additionalServices;
    },
    async handleSave() {
      if (!this.localState) return;
      if (this.hasUnsavedChanges) {
        await this.saveSection({
          homeworkHelp: this.localState.homeworkHelp,
          comfortableWithPets: this.localState.comfortableWithPets,
          infantCare: this.localState.infantCare,
          specialNeeds: this.localState.specialNeeds,
          accessToTransportation: this.localState.accessToTransportation,
          additionalServices: this.buildAdditionalServices(),
          infantCareExperience: this.localState.infantCareExperience || null,
          infantCareServices: [...this.localState.infantCareServices],
        });
      }
    },
  },
};
</script>
